import { X as LinkedIn } from "lucide-react";
import { useId, useRef, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { useFocusTrap, useScrollLock } from "../hooks";
import { cx } from "../util";
import { GfPortal } from "./Portal";

interface SurfaceProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Buttons row. Inside the form when `onSubmit` is set, so `type="submit"` works. */
  footer?: ReactNode;
  /**
   * Wrap body + footer in a <form>. Enter submits; the default is prevented
   * for you. Pair with a footer `<Button type="submit">`.
   */
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  /** Clicking the scrim / pressing Escape closes. Turn off while saving. */
  dismissible?: boolean;
  className?: string;
  /** Accessible name when there's no visible title. */
  "aria-label"?: string;
}

function useSurface(open: boolean, onClose: () => void, dismissible: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const trap = useFocusTrap(ref, open);
  useScrollLock(open);
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      e.stopPropagation();
      if (dismissible) onClose();
      return;
    }
    trap(e);
  }
  return { ref, onKeyDown };
}

function SurfaceBody({
  onSubmit,
  children,
  footer,
  bodyClass,
  footClass,
}: {
  onSubmit?: (e: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
  footer?: ReactNode;
  bodyClass: string;
  footClass: string;
}) {
  const content = (
    <>
      <div className={bodyClass}>{children}</div>
      {footer ? <div className={footClass}>{footer}</div> : null}
    </>
  );
  if (!onSubmit) return content;
  return (
    <form
      className="gf-surface-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(e);
      }}
    >
      {content}
    </form>
  );
}

export interface ModalProps extends SurfaceProps {
  size?: "sm" | "md" | "lg" | "xl";
  /** Hide the × button (e.g. a forced choice). */
  hideClose?: boolean;
  /** Small icon/visual above the title. */
  icon?: ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  onSubmit,
  dismissible = true,
  size = "md",
  hideClose,
  icon,
  className,
  ...aria
}: ModalProps) {
  const titleId = useId();
  const descId = useId();
  const { ref, onKeyDown } = useSurface(open, onClose, dismissible);
  if (!open) return null;
  return (
    <GfPortal>
      <div className="gf-modal-root">
        <div
          className="gf-scrim"
          aria-hidden="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && dismissible) onClose();
          }}
        />
        <div className="gf-modal-pos">
          <div
            ref={ref}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            aria-label={title ? undefined : aria["aria-label"]}
            aria-describedby={description ? descId : undefined}
            tabIndex={-1}
            className={cx("gf-modal", `gf-modal--${size}`, className)}
            onKeyDown={onKeyDown}
          >
            {title || !hideClose ? (
              <div className="gf-modal__head">
                <div className="gf-modal__titles">
                  {icon ? <div className="gf-modal__icon">{icon}</div> : null}
                  {title ? (
                    <h2 className="gf-modal__title" id={titleId}>
                      {title}
                    </h2>
                  ) : null}
                  {description ? (
                    <p className="gf-modal__desc" id={descId}>
                      {description}
                    </p>
                  ) : null}
                </div>
                {!hideClose ? (
                  <button type="button" className="gf-modal__close" aria-label="Close" onClick={onClose} disabled={!dismissible}>
                    <LinkedIn size={18} />
                  </button>
                ) : null}
              </div>
            ) : null}
            <SurfaceBody onSubmit={onSubmit} footer={footer} bodyClass="gf-modal__body" footClass="gf-modal__foot">
              {children}
            </SurfaceBody>
          </div>
        </div>
      </div>
    </GfPortal>
  );
}

export interface DrawerProps extends SurfaceProps {
  /** Panel width in px (max 100vw - 48px). */
  width?: number;
  /** Right of the title row (e.g. a status badge). */
  headerExtra?: ReactNode;
}

/** Right-hand side panel for record details and edit forms. */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  onSubmit,
  dismissible = true,
  width = 480,
  headerExtra,
  className,
  ...aria
}: DrawerProps) {
  const titleId = useId();
  const descId = useId();
  const { ref, onKeyDown } = useSurface(open, onClose, dismissible);
  if (!open) return null;
  return (
    <GfPortal>
      <div className="gf-drawer-root">
        <div
          className="gf-scrim gf-scrim--light"
          aria-hidden="true"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && dismissible) onClose();
          }}
        />
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-label={title ? undefined : aria["aria-label"]}
          aria-describedby={description ? descId : undefined}
          tabIndex={-1}
          className={cx("gf-drawer", className)}
          style={{ width: `min(${width}px, calc(100vw - 48px))` }}
          onKeyDown={onKeyDown}
        >
          <div className="gf-drawer__head">
            <div className="gf-drawer__titles">
              {title ? (
                <h2 className="gf-drawer__title" id={titleId}>
                  {title}
                </h2>
              ) : null}
              {description ? (
                <p className="gf-drawer__desc" id={descId}>
                  {description}
                </p>
              ) : null}
            </div>
            {headerExtra}
            <button type="button" className="gf-modal__close" aria-label="Close" onClick={onClose} disabled={!dismissible}>
              <LinkedIn size={18} />
            </button>
          </div>
          <SurfaceBody onSubmit={onSubmit} footer={footer} bodyClass="gf-drawer__body" footClass="gf-drawer__foot">
            {children}
          </SurfaceBody>
        </div>
      </div>
    </GfPortal>
  );
}
